import { useEffect, useState, useMemo, useCallback } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";
import { useQueryClient } from "@tanstack/react-query";
import { format, compareAsc, addDays } from "date-fns";

import {
  MileageFormInterface,
  AssetModel,
  MileageModel,
  PeriodModel,
  UserPeriods,
  DropdownItemModel,
} from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import { QUERY_WIDTHS, mileageSchema, filterAssetsByRange } from "@utils/index";
import {
  useAssetGetBusinessAssets,
  useMileageCreate,
  useMileageUpdate,
  useUserGetOpenPeriods,
  useTransactionFindPeriod,
} from "@providers/index";
import { useBusinessType, useNotify } from "@hooks/index";
import {
  Drawer,
  DrawerHeader,
  Form,
  FormRow,
  RangePicker,
  FormInput,
  Button,
  DeviceFloppySvg,
  CheckupListSvg,
  XSvg,
  AssetSelector,
  FormText,
  Dropdown,
  Modal,
} from "@components/index";
import { AssetForm } from "@pages/index";
import AlertImg from "@assets/images/alert-triangle.svg";

export const MileageForm = ({
  isUpdating = false,
  businessId,
  mileage,
  isDiBusiness = false,
  onClose,
}: {
  isUpdating?: boolean;
  businessId: string;
  mileage?: MileageModel;
  isDiBusiness: boolean;
  onClose: () => void;
}) => {
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const queryClient = useQueryClient();

  const businessType = useBusinessType(businessId);
  const [assets, setAssets] = useState<AssetModel[]>([]);
  const [taxYearPeriods, setTaxYearPeriods] = useState<UserPeriods>();

  const [assetId, setAssetId] = useState("");
  const [dateFrom, setDateFrom] = useState<Date>();
  const [dateTo, setDateTo] = useState<Date>();
  const [taxYearPeriod, setTaxYearPeriod] = useState<PeriodModel>();
  const [userPeriod, setUserPeriod] = useState<PeriodModel>();

  const [modalDuplicated, setModalDuplicated] = useState(false);

  const taxYearPeriodsItems: DropdownItemModel[] = useMemo(
    () =>
      (taxYearPeriods?.periods || []).map(({ title, startDate, endDate }) => ({
        id: title,
        title: `${title} (${format(new Date(startDate), "dd.MM.yy")} - ${format(new Date(endDate), "dd.MM.yy")})`,
      })),
    [taxYearPeriods],
  );

  const dateStr = useMemo(() => {
    if (!dateTo) {
      return;
    }

    let dateStr = `${dateTo.getFullYear()}`;
    dateStr += "-";
    dateStr += `0${dateTo.getMonth() + 1}`.slice(-2);
    dateStr += "-";

    return (dateStr += `0${dateTo.getDate()}`.slice(-2));
  }, [dateTo]);

  const {
    isPending: isPendingGetPeriods,
    isSuccess: isSuccessGetPeriods,
    isStale: isStaleGetPeriods,
    data: dataGetPeriods,
    isError: isErrorGetPeriods,
    error: errorGetPeriods,
  } = useUserGetOpenPeriods();

  useEffect(() => {
    if (isSuccessGetPeriods && dataGetPeriods) {
      setTaxYearPeriods(dataGetPeriods);
    }
  }, [isSuccessGetPeriods, dataGetPeriods, mileage]);

  useEffect(() => {
    if (isErrorGetPeriods && errorGetPeriods) {
      notify("error", errorGetPeriods.message);
    }
  }, [isErrorGetPeriods, errorGetPeriods, notify]);

  const {
    isPending: isPendingFindPeriod,
    isSuccess: isSuccessFindPeriod,
    isStale: isStaleFindPeriod,
    data: dataFindPeriod,
    isError: isErrorFindPeriod,
    error: errorFindPeriod,
  } = useTransactionFindPeriod({ date: dateStr });

  useEffect(() => {
    if (isSuccessFindPeriod && dataFindPeriod) {
      setUserPeriod(dataFindPeriod);
    }
  }, [isSuccessFindPeriod, dataFindPeriod]);

  useEffect(() => {
    if (isErrorFindPeriod && errorFindPeriod) {
      setUserPeriod(undefined);
    }
  }, [isErrorFindPeriod, errorFindPeriod]);

  const {
    isPending: isPendingGetAssets,
    isSuccess: isSuccessGetAssets,
    data: dataGetAssets,
    isError: isErrorGetAssets,
    error: errorGetAssets,
  } = useAssetGetBusinessAssets(businessId);

  useEffect(() => {
    if (isSuccessGetAssets && dataGetAssets) {
      setAssets(dataGetAssets);
    }
  }, [isSuccessGetAssets, dataGetAssets]);

  useEffect(() => {
    if (isErrorGetAssets && errorGetAssets) {
      notify("error", errorGetAssets.message);
    }
  }, [isErrorGetAssets, errorGetAssets, notify]);

  const { mutate: mutateCreateMileage, isPending: isPendingCreateMileage } =
    useMileageCreate(
      (pressedCreateNewBtn, previousDateTo) => {
        notify("success", "Mileage created successfully.");
        setState({ isLoading: false });
        queryClient.invalidateQueries({
          queryKey: ["mileageGetAll", businessId],
        });
        if (pressedCreateNewBtn) {
          const date = addDays(previousDateTo, 1);
          setDateFrom(date);
          setValue("dateFrom", date);
          setDateTo(date);
          setValue("dateTo", date);
          const taxYearPeriodValue = taxYearPeriods?.periods.find(
            ({ startDate, endDate }) =>
              compareAsc(new Date(startDate), new Date(date)) <= 0 &&
              compareAsc(new Date(endDate), new Date(date)) >= 0,
          );
          setTaxYearPeriod(taxYearPeriodValue);
          pickAsset({ startDate: date, endDate: date, assets });
          reset({
            businessId,
            isDiBusiness,
            businessMiles: "",
            privateMiles: "",
            comments: "",
          });
        } else {
          handleClose();
        }
      },
      ({ message, fields }) => {
        if (
          message.includes(
            "The selected date range overlaps with an existing mileage log.",
          )
        ) {
          setModalDuplicated(true);
          return;
        }
        Object.keys(fields || {}).forEach((key) =>
          setError(
            key as
              | "businessId"
              | "assetId"
              | "dateFrom"
              | "dateTo"
              | "comments"
              | "businessMiles"
              | "privateMiles",
            {
              message: fields![key],
            },
          ),
        );
        notify("error", message);
      },
    );

  const { mutate: mutateUpdateMileage, isPending: isPendingUpdateMileage } =
    useMileageUpdate(
      (pressedCreateNewBtn, previousDateTo) => {
        notify("success", "Mileage updated successfully.");
        setState({ isLoading: false });
        queryClient.invalidateQueries({
          queryKey: ["mileageGetAll", businessId],
        });
        if (pressedCreateNewBtn) {
          const date = addDays(previousDateTo, 1);
          setDateFrom(date);
          setValue("dateFrom", date);
          setDateTo(date);
          setValue("dateTo", date);
          const taxYearPeriodValue = taxYearPeriods?.periods.find(
            ({ startDate, endDate }) =>
              compareAsc(new Date(startDate), new Date(date)) <= 0 &&
              compareAsc(new Date(endDate), new Date(date)) >= 0,
          );
          setTaxYearPeriod(taxYearPeriodValue);
          pickAsset({ startDate: date, endDate: date, assets });
          reset({
            businessId,
            isDiBusiness,
            businessMiles: "",
            privateMiles: "",
            comments: "",
          });
        } else {
          handleClose();
        }
      },
      ({ message, fields }) => {
        if (
          message.includes(
            "The selected date range overlaps with an existing mileage log.",
          )
        ) {
          setModalDuplicated(true);
          return;
        }
        Object.keys(fields || {}).forEach((key) =>
          setError(
            key as
              | "businessId"
              | "assetId"
              | "dateFrom"
              | "dateTo"
              | "comments"
              | "businessMiles"
              | "privateMiles",
            {
              message: fields![key],
            },
          ),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setState({
      isLoading:
        (isStaleGetPeriods && isPendingGetPeriods) ||
        (isStaleFindPeriod && isPendingFindPeriod) ||
        isPendingGetAssets ||
        isPendingCreateMileage ||
        isPendingUpdateMileage,
    });
  }, [
    isStaleGetPeriods,
    isPendingGetPeriods,
    isStaleFindPeriod,
    isPendingFindPeriod,
    isPendingGetAssets,
    isPendingCreateMileage,
    isPendingUpdateMileage,
    setState,
  ]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    resetField,
    clearErrors,
    setError,
    setValue,
  } = useForm<MileageFormInterface>({
    mode: "onBlur",
    disabled:
      (isStaleGetPeriods && isPendingGetPeriods) ||
      (isStaleFindPeriod && isPendingFindPeriod) ||
      isPendingGetAssets ||
      isPendingCreateMileage ||
      isPendingUpdateMileage,
    resolver: yupResolver(mileageSchema),
  });

  const pickAsset = useCallback(
    ({
      startDate,
      endDate,
      assetId,
      assets,
    }: {
      startDate?: Date;
      endDate?: Date;
      assetId?: string;
      assets: AssetModel[];
    }) => {
      const filteredAssets = filterAssetsByRange({
        assets,
        startDate,
        endDate,
        excludeTypes: ["other", "rental_property"],
      });
      const defaultAsset = filteredAssets.find(({ isDefault }) => isDefault);
      if (
        assetId &&
        !filteredAssets.find(({ uniqueId }) => uniqueId === assetId)
      ) {
        setAssetId("");
        setValue("assetId", "");
        clearErrors("assetId");
      } else if (!assetId && defaultAsset) {
        setAssetId(defaultAsset.uniqueId);
        setValue("assetId", defaultAsset.uniqueId);
        clearErrors("assetId");
      } else if (
        !assetId &&
        filteredAssets.filter(({ disposal }) => !disposal).length === 1
      ) {
        setAssetId(
          filteredAssets.filter(({ disposal }) => !disposal)[0].uniqueId,
        );
        setValue(
          "assetId",
          filteredAssets.filter(({ disposal }) => !disposal)[0].uniqueId,
        );
        clearErrors("assetId");
      }
    },
    [setValue, clearErrors],
  );

  useEffect(() => {
    if (isUpdating && mileage) {
      setAssetId(mileage.asset.uniqueId);
      const taxYearPeriodValue = taxYearPeriods?.periods.find(
        ({ startDate, endDate }) =>
          compareAsc(new Date(startDate), new Date(mileage.dateFrom)) <= 0 &&
          compareAsc(new Date(endDate), new Date(mileage.dateTo)) >= 0,
      );
      setTaxYearPeriod(taxYearPeriodValue);
      setDateFrom(new Date(mileage.dateFrom));
      setValue("dateFrom", new Date(mileage.dateFrom));
      setDateTo(new Date(mileage.dateTo));
      setValue("dateTo", new Date(mileage.dateTo));
      reset({
        businessId,
        assetId: mileage.asset.uniqueId,
        businessMiles: mileage.businessMiles,
        isDiBusiness,
        privateMiles: mileage.privateMiles,
        comments: mileage.comments || "",
      });
    }
  }, [
    isUpdating,
    mileage,
    reset,
    setValue,
    businessId,
    isDiBusiness,
    taxYearPeriods,
  ]);

  useEffect(() => {
    if (!isUpdating && !mileage) {
      setTaxYearPeriod(taxYearPeriods?.currentPeriod);
      setValue("isDiBusiness", isDiBusiness);
      pickAsset({ assets });
    }
  }, [
    isUpdating,
    mileage,
    setValue,
    isDiBusiness,
    pickAsset,
    assets,
    taxYearPeriods,
  ]);

  const handleSelectPeriod = (id: string) => {
    const newPeriod = taxYearPeriods?.periods?.find(
      ({ title }) => title === id,
    );
    setTaxYearPeriod(newPeriod);
    setDateFrom(undefined);
    resetField("dateFrom");
    clearErrors("dateFrom");
    setDateTo(undefined);
    resetField("dateTo");
    clearErrors("dateTo");
    pickAsset({ assetId, assets });
  };

  const handleSelectDateFrom = (value: string) => {
    const date = value ? new Date(value) : undefined;
    setDateFrom(date);
    resetField("dateFrom");
    if (date) {
      setValue("dateFrom", date);
    }
    clearErrors("dateFrom");
    pickAsset({ startDate: date, endDate: dateTo, assetId, assets });
  };

  const handleSelectDateTo = (value: string) => {
    const date = value ? new Date(value) : undefined;
    setDateTo(date);
    resetField("dateTo");
    if (date) {
      setValue("dateTo", date);
    }
    clearErrors("dateTo");
    pickAsset({ startDate: dateFrom, endDate: date, assetId, assets });
  };

  const handleSelectAsset = (assetId: string) => {
    setAssetId(assetId);
    setValue("assetId", assetId);
    clearErrors("assetId");
  };

  const onSubmit: SubmitHandler<MileageFormInterface> = (data) =>
    !isUpdating
      ? mutateCreateMileage({ ...data })
      : mutateUpdateMileage({ mileageId: mileage?.uniqueId, ...data });

  const handleClose = () => {
    setState({ isDrawerShown: false });
    onClose();
  };

  return (
    <Drawer className="drawer-mileage-form">
      <DrawerHeader
        onClose={handleClose}
        title={
          <div className="text-300 font-bold text-black-100 md:text-600">
            {!isUpdating ? "Log" : "Edit"} Mileage
          </div>
        }
      />
      <Form
        className="max-h-[calc(100dvh_-_50px)] overflow-y-auto px-4 md:max-h-[calc(100dvh_-_104px)] md:px-14"
        onSubmit={handleSubmit(onSubmit)}
      >
        <input
          {...register<"pressedCreateNewBtn">("pressedCreateNewBtn")}
          type="hidden"
          name="pressedCreateNewBtn"
        />
        <input
          {...register<"businessId">("businessId")}
          type="hidden"
          name="businessId"
          value={businessId}
        />
        <input
          {...register<"isDiBusiness">("isDiBusiness")}
          type="hidden"
          name="isDiBusiness"
        />
        <FormRow top={"small"}>
          <Dropdown
            items={taxYearPeriodsItems}
            label="Tax Year"
            value={taxYearPeriod?.title}
            onSelect={handleSelectPeriod}
            error={
              formState.isSubmitted && !taxYearPeriod
                ? "The Tax Year field is required."
                : ""
            }
            dataTestid="input_taxYear"
          />
        </FormRow>
        <FormRow top="small" className="relative gap-y-3">
          <RangePicker
            parent=".drawer-mileage-form"
            startDate={dateFrom ? format(dateFrom, "yyyy-MM-dd") : ""}
            endDate={dateTo ? format(dateTo, "yyyy-MM-dd") : ""}
            prefix="mileageForm"
            onSetStartDate={handleSelectDateFrom}
            onSetEndDate={handleSelectDateTo}
            showPeriods={false}
            disabled={!taxYearPeriod}
            variant="formField"
            calendarContainerClassName="!right-0"
            minDate={
              taxYearPeriod?.startDate
                ? new Date(taxYearPeriod.startDate)
                : undefined
            }
            maxDate={
              taxYearPeriod?.endDate
                ? new Date(taxYearPeriod.endDate)
                : undefined
            }
            error={
              formState.errors.dateFrom?.message ||
              formState.errors.dateTo?.message
            }
            dataTestid="input_dateRange"
          />
          <input
            {...register<"dateFrom">("dateFrom")}
            type="hidden"
            name="dateFrom"
          />
          <input
            {...register<"dateTo">("dateTo")}
            type="hidden"
            name="dateTo"
          />
          {userPeriod && (
            <div
              className="border-l-4 border-l-primary-300 bg-primary-104 px-4 py-2"
              data-testid="label_period"
            >
              <div className="text-100 text-black-54">Period</div>
              <div className="pt-2 text-black-100">{userPeriod.title}</div>
            </div>
          )}
        </FormRow>
        <FormRow top="small">
          <AssetSelector
            businessType={businessType}
            items={assets}
            filteringType="range"
            startDate={dateFrom}
            endDate={dateTo}
            excludeTypes={["other", "rental_property"]}
            hideNotAssetSpecific
            value={assetId}
            error={formState.errors.assetId?.message}
            onSetAsset={handleSelectAsset}
            dataTestid="input_asset"
          />
          <input
            {...register<"assetId">("assetId")}
            type="hidden"
            name="assetId"
          />
        </FormRow>
        <FormRow
          top={isMobile ? "large" : "medium"}
          className="text-300 font-bold md:text-400"
        >
          Mileage Details
        </FormRow>
        <FormRow top={"small"}>
          <FormInput
            {...register<"businessMiles">("businessMiles")}
            type="number"
            name="businessMiles"
            label="Business Miles"
            error={formState.errors.businessMiles?.message}
            onBlur={({ currentTarget }) =>
              setValue("businessMiles", currentTarget.value.trim())
            }
            onChange={() => clearErrors("businessMiles")}
            data-testid="input_businessMiles"
          />
        </FormRow>
        {isDiBusiness && (
          <FormRow top={"small"}>
            <FormInput
              {...register<"privateMiles">("privateMiles")}
              type="number"
              name="privateMiles"
              label="Private Miles"
              error={formState.errors.privateMiles?.message}
              onBlur={({ currentTarget }) =>
                setValue("privateMiles", currentTarget.value.trim())
              }
              onChange={() => clearErrors("privateMiles")}
              data-testid="input_privateMiles"
            />
          </FormRow>
        )}
        <FormRow top={"small"}>
          <FormText
            {...register<"comments">("comments")}
            name="comments"
            label="Comments"
            error={formState.errors.comments?.message}
            onChange={() => clearErrors("comments")}
            data-testid="input_comment"
          />
        </FormRow>
        <FormRow
          isRow={!isMobile}
          isBetween
          top="small"
          className="gap-y-4 pb-6"
        >
          <div
            className="flex flex-col gap-4 md:flex-row"
            data-testid="button_saveMileage"
          >
            <Button
              type="submit"
              title="Save"
              leftIcon={<DeviceFloppySvg />}
              className="w-full md:w-fit md:px-5"
              height="small"
            />
            <Button
              type="submit"
              title="Save and Add Another"
              leftIcon={<CheckupListSvg />}
              height="small"
              variant="secondary"
              onClick={() => setValue("pressedCreateNewBtn", true)}
            />
          </div>
          <Button
            title="Cancel"
            variant="secondary"
            height="small"
            leftIcon={<XSvg />}
            onClick={handleClose}
          />
        </FormRow>
      </Form>
      {assetId === "new" && (
        <AssetForm
          initialBusinessId={businessId}
          excludeTypes={["rental_property", "other"]}
          onClose={() => {
            setAssetId("");
            setValue("assetId", "");
            clearErrors("assetId");
          }}
          onCreated={handleSelectAsset}
        />
      )}
      <Modal show={modalDuplicated} onClose={() => setModalDuplicated(false)}>
        <div className="flex flex-col items-center text-center">
          <img
            src={AlertImg}
            alt="Alert"
            className="h-12 w-12 md:h-14 md:w-14"
          />
          <div className="pt-2 text-400 font-bold text-black-100 md:pt-4 md:text-500">
            Alert!
          </div>
          <div className="pt-1 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            Please revise the date range, as it overlaps with an existing log
            entry.
          </div>
        </div>
        <div className="flex justify-between gap-x-2 pt-8 md:gap-x-8 md:pt-12">
          <Button
            type="button"
            variant="primary"
            title="OK"
            className="mx-auto w-40"
            onClick={() => setModalDuplicated(false)}
          />
        </div>
      </Modal>
    </Drawer>
  );
};
